"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { TrendingUp, Wand2, ShieldCheck, ArrowRight } from "lucide-react";
import { Button, Typography } from "@drpcrd/shared/ui";
import { cn } from "@drpcrd/utils";

const MESSAGES = [
  "Show #grwm posts featuring Starbucks coffee",
  "Who are the top performing UK beauty creators in the last 7 days",
  "Find me street interviewers who don’t swear too much",
  "Run a brand safety check on a creator for me",
  "Write me a post-campaign report for my CMO",
];

const QUICK_LINKS = [
  { label: "Track competitors", icon: TrendingUp },
  { label: "Brainstorm ideas", icon: Wand2 },
  { label: "Run safety check", icon: ShieldCheck },
];

const TYPING_SPEED_MS = 50;
const PAUSE_MS = 2000;
const DELETE_SPEED_MS = 25;

/**
 * Animated chat input that types through a series of AI prompts, looping continuously.
 */
export const FloatingChat = () => {
  const router = useRouter();
  const [messageIndex, setMessageIndex] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const target = MESSAGES[messageIndex];

    if (!isDeleting && displayed === target) {
      const pause = setTimeout(() => setIsDeleting(true), PAUSE_MS);
      return () => clearTimeout(pause);
    }

    if (isDeleting && displayed === "") {
      setIsDeleting(false);
      setMessageIndex((i) => (i + 1) % MESSAGES.length);
      return;
    }

    const timeout = setTimeout(
      () => {
        setDisplayed((prev) =>
          isDeleting ? prev.slice(0, -1) : target.slice(0, prev.length + 1),
        );
      },
      isDeleting ? DELETE_SPEED_MS : TYPING_SPEED_MS,
    );

    return () => clearTimeout(timeout);
  }, [displayed, isDeleting, messageIndex]);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => router.push("/contact")}
      onKeyDown={(e) => e.key === "Enter" && router.push("/contact")}
      className={cn(
        "border-primary-800 cursor-pointer rounded-2xl border-2 bg-white p-2 transition-all duration-700 ease-out sm:p-3",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
      )}
    >
      {/* Removed shadow for no floating effect */}
      <div className="flex flex-col gap-1 sm:gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex lg:flex-1 flex-col gap-7">
          <Typography size="base" color="black" className="line-clamp-2 h-5 overflow-hidden text-sm lg:h-auto lg:line-clamp-none lg:overflow-visible lg:text-lg lg:leading-3">
            {displayed}
            <span className="animate-pulse">|</span>
          </Typography>
          <div className="hidden flex-wrap gap-1 lg:flex">
            {QUICK_LINKS.map(({ label, icon: Icon }) => (
              <Button
                key={label}
                as="div"
                variant="outlined"
                size="md"
                icon={Icon}
                iconPosition="start"
                className="rounded-full"
                aria-label={label}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <Button
          as={Link}
          href="/contact"
          variant="filled"
          size="md"
          icon={ArrowRight}
          iconPosition="end"
          className="shrink-0 self-end max-sm:gap-0.75 max-sm:px-1.5 max-sm:text-xs max-sm:leading-[14px] max-sm:[&>svg]:size-1.5"
        >
          Start now
        </Button>
      </div>
    </div>
  );
};
